import { type CSSProperties, useEffect, useId, useState } from "react";

// ---
// The Flexcell mark, moved the way the editor moves it
//
// Ported from the Flexcell editor's v1.2 artboard, which animates the mark with two CSS
// transitions rather than a keyframe loop: on a toggle the four stars converge on the centre and
// the whole mark turns, and half a second later they split back out and it turns again.
//
// Transitions, not keyframes: the mark reports a change of state. `FlexcellSpinner` below repeats
// the same beat for waiting, so the console has one mark that moves one way rather than two
// vocabularies to learn.
// ---

/** Each cell's star, and where it travels when the four merge — in viewBox units. */
const STARS = [
  { d: "M6.5 1Q6.5 6.5 12 6.5Q6.5 6.5 6.5 12Q6.5 6.5 1 6.5Q6.5 6.5 6.5 1Z", dx: 5.5, dy: 5.5 },
  {
    d: "M17.5 1Q17.5 6.5 23 6.5Q17.5 6.5 17.5 12Q17.5 6.5 12 6.5Q17.5 6.5 17.5 1Z",
    dx: -5.5,
    dy: 5.5,
  },
  {
    d: "M6.5 12Q6.5 17.5 12 17.5Q6.5 17.5 6.5 23Q6.5 17.5 1 17.5Q6.5 17.5 6.5 12Z",
    dx: 5.5,
    dy: -5.5,
  },
  {
    d: "M17.5 12Q17.5 17.5 23 17.5Q17.5 17.5 17.5 23Q17.5 17.5 12 17.5Q17.5 17.5 17.5 12Z",
    dx: -5.5,
    dy: -5.5,
  },
] as const;

/**
 * How long one half of the gesture takes: four gathering into one, or one parting into four.
 *
 * The editor's own half second, and the default everywhere. It is a parameter rather than a
 * constant because the two uses want different speeds: the sidebar reports something that has
 * already happened and should keep up with it, while waiting is better watched than hurried.
 *
 * The CSS duration is derived from this number rather than written beside it — the part is
 * scheduled when the gather is due to land, so a transition that disagreed would either cut the
 * gather short or leave the mark sitting at one.
 */
export const MERGE_MS = 500;

/** Slower, for waiting: a loop at the sidebar's pace reads as urgency nobody asked for. */
export const LOADING_MS = 700;

const ease = (ms: number) => `transform ${ms}ms cubic-bezier(.45,0,.55,1)`;

/**
 * How long the mark rests as four before gathering again.
 *
 * The rest belongs at four and nowhere else: one is the middle of the gesture, not a place to
 * sit, so the mark passes through it without pausing. Without a rest at four the next gather
 * begins on the frame the last one lands, and a loop of them reads as one hurried blur rather
 * than as a thing that keeps gathering and letting go.
 */
const REST_MS = 800;

/** What a merged star does: in to the centre, half again as big, and a quarter turn. */
const merge = (dx: number, dy: number) => `translate(${dx}px, ${dy}px) scale(1.7) rotate(45deg)`;

export type FlexcellMarkProps = {
  /** Rendered size in px. The mark is square. */
  size?: number;
  /** Fill colour — defaults to `currentColor` so it inherits the surrounding text colour. */
  color?: string;
  /**
   * Turns taken so far.
   *
   * A count rather than a flag: the mark turns a further 360° each time it goes up, so the same
   * value twice is no movement and every increment is one turn — which is what lets a merge and
   * the split that follows it read as two beats of one gesture.
   */
  spin?: number;
  /** Whether the four are currently gathered at the centre. */
  merged?: boolean;
  /** How long a gather or a part takes. Must match whatever schedules the two. */
  durationMs?: number;
  className?: string;
  style?: CSSProperties;
  /** Accessible label. */
  title?: string;
};

export function FlexcellMark({
  size = 24,
  color = "currentColor",
  spin = 0,
  merged = false,
  durationMs = MERGE_MS,
  className,
  style,
  title = "Flexcell",
}: FlexcellMarkProps) {
  const uid = useId().replace(/:/g, "");
  return (
    <svg
      className={className === undefined ? `fx-${uid}` : `fx-${uid} ${className}`}
      style={{
        transform: `rotate(${spin * 360}deg)`,
        transition: ease(durationMs),
        flex: "none",
        ...style,
      }}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      role="img"
      aria-label={title}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{title}</title>
      <style>{`
        @media (prefers-reduced-motion: reduce) {
          .fx-${uid}, .fx-${uid} path { transition: none; }
        }
      `}</style>
      <g fill={color}>
        {STARS.map((star) => (
          <path
            key={star.d}
            d={star.d}
            style={{
              transformBox: "fill-box",
              transformOrigin: "center",
              transform: merged ? merge(star.dx, star.dy) : "none",
              transition: ease(durationMs),
            }}
          />
        ))}
      </g>
    </svg>
  );
}

/**
 * One beat of the gesture: the four gather and the mark turns, then half a second later they
 * part and it turns again.
 *
 * `trigger` is a count, not a flag — the mark turns a further 360° each time it rises, so the
 * same value twice is no movement and every increment is one beat. Zero plays nothing, which is
 * what a first render should do when nothing has happened yet.
 */
export function useMergeBeat(
  trigger: number,
  durationMs: number = MERGE_MS,
): { spin: number; merged: boolean } {
  const [merged, setMerged] = useState(false);
  const [spin, setSpin] = useState(0);

  useEffect(() => {
    if (trigger === 0) return;
    setMerged(true);
    setSpin((turns) => turns + 1);
    const part = window.setTimeout(() => {
      setMerged(false);
      setSpin((turns) => turns + 1);
    }, durationMs);
    return () => window.clearTimeout(part);
  }, [trigger, durationMs]);

  return { spin, merged };
}

/**
 * The same gesture, repeated while something is running.
 *
 * Waiting and collapsing the sidebar are the same motion on purpose: one mark, one way of
 * moving, so the vocabulary is not learnt twice. The sidebar plays it once because it reports a
 * change that has finished; this keeps playing it because what it reports has not.
 *
 * A cycle is four to one and back to four, then a rest as four — so what repeats is the whole
 * gesture, never half of it.
 */
export function FlexcellSpinner({
  size = 24,
  color = "currentColor",
  title = "Loading",
  durationMs = LOADING_MS,
  restMs = REST_MS,
}: Pick<FlexcellMarkProps, "size" | "color" | "title" | "durationMs"> & {
  /** The still beat between one cycle and the next. */
  restMs?: number;
}) {
  // One on mount, then another every full cycle: gather, part, and the rest at four. Driving
  // `useMergeBeat` rather than repeating it is what keeps this identical to the sidebar's.
  const [beat, setBeat] = useState(1);
  useEffect(() => {
    const next = window.setInterval(() => setBeat((count) => count + 1), durationMs * 2 + restMs);
    return () => window.clearInterval(next);
  }, [durationMs, restMs]);
  const { spin, merged } = useMergeBeat(beat, durationMs);

  return (
    <FlexcellMark
      size={size}
      color={color}
      title={title}
      spin={spin}
      merged={merged}
      durationMs={durationMs}
    />
  );
}
